import { intentHue } from '../../shared/intent';
import { formatPhone } from '../../shared/phone';
import type { VoicemailListItem } from '../../types/voicemail';
import type { Hue } from '../../ui/v2';

export type Folder = 'inbox' | 'archived';
export const FOLDERS: Folder[] = ['inbox', 'archived'];
export const FOLDER_LABEL: Record<Folder, string> = {
  inbox: 'Inbox',
  archived: 'Archived',
};

export function folderFromParams(params: URLSearchParams): Folder {
  return params.get('folder') === 'archived' ? 'archived' : 'inbox';
}

/** Read voicemails older than this are auto-archived server-side. */
export const RECENT_WINDOW_MS = 48 * 60 * 60 * 1000;

export type Bucket = 'recent' | 'earlier' | 'archived';
export const BUCKET_LABEL: Record<Bucket, string> = {
  recent: 'Last 48 hours',
  earlier: 'Earlier',
  archived: 'Archived',
};

export function bucketOf(
  v: Pick<VoicemailListItem, 'call_time'>,
  folder: Folder,
  now = Date.now(),
): Bucket {
  if (folder === 'archived') return 'archived';
  return now - new Date(v.call_time).getTime() <= RECENT_WINDOW_MS
    ? 'recent'
    : 'earlier';
}

/** Consecutive runs of rows by bucket, in list order. */
export function groupByBucket<T extends Pick<VoicemailListItem, 'call_time'>>(
  rows: T[],
  folder: Folder,
  now = Date.now(),
): { bucket: Bucket; items: T[] }[] {
  const groups: { bucket: Bucket; items: T[] }[] = [];
  for (const row of rows) {
    const bucket = bucketOf(row, folder, now);
    const group = groups.find((g) => g.bucket === bucket);
    if (group) group.items.push(row);
    else groups.push({ bucket, items: [row] });
  }
  return groups;
}

export function callerDisplayName(
  v: Pick<VoicemailListItem, 'vip_name' | 'caller_name'>,
): string {
  return v.vip_name?.trim() || v.caller_name?.trim() || '';
}

export function callerLabel(
  v: Pick<VoicemailListItem, 'vip_name' | 'caller_name' | 'caller_phone'>,
): string {
  return (
    callerDisplayName(v) || formatPhone(v.caller_phone) || 'Unknown caller'
  );
}

/**
 * The stored category "Voice Mail" is shown as the product word "Voicemail".
 * Other category names are the store's own labels and stay as written.
 */
export function categoryLabel(category: string | null | undefined): string {
  const trimmed = category?.trim() ?? '';
  if (/^voice[\s_-]*mail$/i.test(trimmed)) return 'Voicemail';
  return trimmed;
}

/**
 * `matched_category` is free text from the store's notification rules, or
 * the literal "Uncategorized". Categories take the shared intent colour;
 * "Uncategorized" is neutral; an empty category shows no tag.
 */
export function categoryTone(
  category: string | null | undefined,
): Hue | 'neutral' | null {
  if (!category?.trim()) return null;
  if (/uncategor/i.test(category)) return 'neutral';
  return intentHue(category);
}
